import {Optional} from '@othree.io/optional'
import {StackAccount} from './core'
import {
    Code,
    FunctionProps,
    IFunction,
    Runtime,
    Tracing,
    Function,
    Alias,
    FunctionAttributes, Architecture,
} from 'aws-cdk-lib/aws-lambda'
import {Duration, Stack} from 'aws-cdk-lib'

export type ScalingConfiguration = Readonly<{
    minCapacity: number
    maxCapacity: number
    utilizationTarget: number
}>

export type LambdaInput = Readonly<{
    functionName: string
    codePath: string
    handler: string
    environment?: {
        [key: string]: string;
    }
    lambdaConfiguration: LambdaConfiguration
    scalingConfiguration?: ScalingConfiguration
    reservedConcurrentExecutions?: number
}>

export type LambdaConfiguration = Partial<FunctionProps> & Readonly<{
    runtime: Runtime
    tracing: Tracing
    timeout: Duration
    memorySize: number
    architecture: Architecture
}>

/** Creates a Lambda function with a versioned alias and optional provisioned concurrency auto-scaling. */
export const withLambda = (deps: {
    stack: Stack
    aliasName: string
}) =>
    (input: LambdaInput): IFunction => {
        const lambdaFunction = new Function(deps.stack, input.functionName, {
            ...input.lambdaConfiguration,
            functionName: input.functionName,
            code: Code.fromAsset(input.codePath),
            handler: input.handler,
            environment: input.environment,
            reservedConcurrentExecutions: input.reservedConcurrentExecutions,
        })

        const alias = new Alias(deps.stack, `${input.functionName}-Alias`, {
            aliasName: deps.aliasName,
            version: lambdaFunction.currentVersion,
        })

        Optional(input.scalingConfiguration).map(scaling => {
            const as = alias.addAutoScaling({
                maxCapacity: scaling.maxCapacity,
                minCapacity: scaling.minCapacity,
            })

            as.scaleOnUtilization({
                utilizationTarget: scaling.utilizationTarget,
            })

            return true
        })

        return alias
    }

export type LambdaImportInput = Readonly<{
    arn: string
    id: string
}>

/** Imports an existing Lambda function by ARN. */
export const importFunction = (deps: { stack: Stack }) =>
    (input: LambdaImportInput, attributes?: Partial<FunctionAttributes>): IFunction => {
        const fn = Function.fromFunctionAttributes(deps.stack, input.id, {
            functionArn: input.arn,
            sameEnvironment: true,
            ...Optional(attributes).orElse({}),
        })

        return fn
    }

/** Constructs a Lambda function ARN from account info, function name, and optional alias. */
export const functionArnFromName = (deps: { account: StackAccount }) =>
    (fnName: string, alias?: string): string => {
        return `arn:aws:lambda:${deps.account.region}:${deps.account.accountId}:function:${fnName}${Optional(alias).map(_ => `:${_}`).orElse('')}`
    }

/** Constructs a Lambda function ARN with a pre-configured alias. */
export const functionArnFromNameAndAlias = (deps: { account: StackAccount, aliasName: string }) =>
    (fnName: string): string => {
        return functionArnFromName(deps)(fnName, deps.aliasName)
    }

/** Imports a Lambda function by partial ID, using injected `withId` and `functionArn` to resolve the full ARN. */
export const importFunctionByPartialId = (deps: {
    functionArn: (fnName: string) => string
    withId: (identifier: string) => string
    importFunction: (input: LambdaImportInput, attributes?: Partial<FunctionAttributes>) => IFunction
}) =>
    (id: string, attributes?: Partial<FunctionAttributes>) => {
        return deps.importFunction({
            id: deps.withId(id),
            arn: deps.functionArn(deps.withId(id)),
        }, attributes)
    }
